"use client";

import React, { useState, useEffect, useRef } from 'react';
import styles from './ReportSidebar.module.css';
import {
    FaChevronDown,
    FaFacebook,
    FaLinkedin,
    FaWhatsapp,
    FaEnvelope
} from 'react-icons/fa';
import { BsTwitterX } from "react-icons/bs";

interface SidebarSection {
    id: string;
    label: string;
}

interface ReportSidebarProps {
    reportId: string;
    reportTitle: string;
    topics?: any[];
    activeTopicId?: string; // New prop for active section highlighting
    activeTab?: string;
    setActiveTab?: (tabId: string) => void;
    setActiveTopicId?: (topicId: string) => void;
    onDownloadSample?: () => void;
    onEnquire?: () => void;
    stickyOffset?: number;
    slug?: string; // Added for share functionality
}

const ReportSidebar: React.FC<ReportSidebarProps> = ({
    reportId,
    reportTitle,
    topics,
    activeTopicId,
    activeTab,
    setActiveTab,
    setActiveTopicId,
    onDownloadSample,
    onEnquire,
    stickyOffset = 260, // Fallback to current hardcoded if not provided
    slug
}) => {
    const [currentUrl, setCurrentUrl] = useState('');
    const [isShareOpen, setIsShareOpen] = useState(false);
    const navListWrapperRef = useRef<HTMLDivElement>(null);
    const activeItemRef = useRef<HTMLLIElement>(null);

    useEffect(() => {
        // Get the current page URL
        setCurrentUrl(window.location.href);
    }, []);

    // Auto-scroll active nav item into view within the sidebar list.
    // Always scroll so the active item is near the top of the nav list wrapper —
    // this ensures "Market Gain" (item 1) returns to the top when scrolling back up.
    useEffect(() => {
        if (activeItemRef.current && navListWrapperRef.current) {
            const wrapper = navListWrapperRef.current;
            const activeItem = activeItemRef.current;
            const itemTop = activeItem.offsetTop;
            const itemBottom = itemTop + activeItem.offsetHeight;
            const wrapperScrollTop = wrapper.scrollTop;
            const wrapperHeight = wrapper.clientHeight;

            const isAbove = itemTop < wrapperScrollTop;
            const isBelow = itemBottom > wrapperScrollTop + wrapperHeight;

            if (isAbove) {
                // Item is above visible area — scroll list up so item is at the top
                wrapper.scrollTo({ top: Math.max(0, itemTop - 8), behavior: 'smooth' });
            } else if (isBelow) {
                // Item is below visible area — scroll list down to reveal it
                wrapper.scrollTo({ top: itemBottom - wrapperHeight + 8, behavior: 'smooth' });
            }
        }
    }, [activeTopicId]);

    // When the user scrolls the PAGE back near the top, force the nav list
    // to scrollTop = 0 so the first item is always visible.  The useEffect
    // above only runs when activeTopicId changes, but the fallback logic can
    // re-highlight the same first section without changing activeTopicId —
    // so this listener ensures the nav list resets even in that case.
    useEffect(() => {
        const handlePageScroll = () => {
            if (!navListWrapperRef.current) return;
            if (window.scrollY < 300 && navListWrapperRef.current.scrollTop > 0) {
                navListWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };
        window.addEventListener('scroll', handlePageScroll, { passive: true });
        return () => window.removeEventListener('scroll', handlePageScroll);
    }, []);

    const sections: SidebarSection[] =
        topics && topics.length > 0
            ? topics
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((t) => ({
                    id: t.topic_id || t.topic_title?.toLowerCase().replace(/\s+/g, '-'),
                    label: t.topic_title || 'Untitled Section'
                }))
            : [];

    const scrollToSection = (sectionId: string) => {
        // Only topics/description content requires tab switching.
        // FAQ is usually outside the tab content (scrollable on any tab).
        const isDescriptionSection = sectionId !== 'faq-section';
        const needsTabSwitch = isDescriptionSection && setActiveTab && activeTab !== 'description';

        if (needsTabSwitch) {
            setActiveTab!('description');
        }

        // Set active topic highlighting
        if (setActiveTopicId) {
            setActiveTopicId(sectionId);
        }

        // Helper that actually performs the scroll using live DOM measurements.
        const doScroll = () => {
            const element = document.getElementById(sectionId);
            if (!element) return;

            // Determine header height based on screen width (matching layout breakpoints)
            const isMobile = window.innerWidth <= 900;
            const headerHeight = isMobile ? 110 : 98;

            const tabsBar = document.querySelector('[class*="tabHeaderList"]');
            const tabsBarHeight = tabsBar ? tabsBar.getBoundingClientRect().height : 60;

            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;

            // Calculate target scroll positions for two states:
            // 1. With sticky report bar visible (final scroll position > 200)
            const offsetSticky = headerHeight + 60 + tabsBarHeight + 12;
            const targetSticky = elementPosition - offsetSticky;

            // 2. With sticky report bar hidden (final scroll position <= 200)
            const offsetStatic = headerHeight + tabsBarHeight + 12;
            const targetStatic = elementPosition - offsetStatic;

            // Choose target based on final predicted scroll position state.
            const offsetPosition = targetSticky > 200 ? targetSticky : Math.max(0, targetStatic);

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        };

        // If we switched tabs, wait for React to re-render and images to start
        // loading before measuring (200ms is more reliable than 100ms).
        setTimeout(doScroll, needsTabSwitch ? 200 : 0);
    };

    const shareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
        xTwitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(reportTitle)}`,
        email: `mailto:?subject=${encodeURIComponent(reportTitle)}&body=${encodeURIComponent("Check out this report: " + currentUrl)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(reportTitle + ' ' + currentUrl)}`
    };

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Close dropdown on click outside or scroll
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        const handleScroll = () => {
            if (isDropdownOpen) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isDropdownOpen]);

    const handleNavigation = (path: string, typeParam: string) => {
        const url = `/${path}?id=${reportId}&name=${encodeURIComponent(reportTitle)}&type=${typeParam}`;
        window.open(url, '_blank');
    };

    return (
        <div className={styles.sidebar}>
            <div className={styles.navContainer}>
                <h3 className={styles.heading}>About This Report</h3>

                <div className={styles.navListWrapper} ref={navListWrapperRef}>
                    {sections.length > 0 ? (
                        <ul className={styles.navList}>
                            {sections.map((section) => (
                                <li key={section.id} ref={activeTopicId === section.id ? activeItemRef : null}>
                                    <button
                                        className={`${styles.navItem} ${activeTopicId === section.id ? styles.active : ''}`}
                                        onClick={() => scrollToSection(section.id)}
                                    >
                                        {section.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className={styles.noTopics}>
                            No topics found
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.actionSection}>
                <button className={styles.btnDownload} onClick={onDownloadSample}>
                    Download Sample
                </button>

                <div className={styles.dropdownWrapper} ref={dropdownRef}>
                    <div className={styles.dropdownHeader} onClick={toggleDropdown}>
                        <button className={styles.btnEnquire} type="button">
                            Enquire About This Report
                            <FaChevronDown className={`${styles.arrowIcon} ${isDropdownOpen ? styles.rotate : ''}`} />
                        </button>
                    </div>

                    {isDropdownOpen && (
                        <div className={styles.dropdownMenu} style={{ display: 'block' }}>
                            <button className={styles.dropdownItem} onClick={() => handleNavigation('sample_request', 'smp')}>
                                Request a Sample
                            </button>
                            <button className={styles.dropdownItem} onClick={() => handleNavigation('Discount', 'discount')}>
                                Request For Discount
                            </button>
                            <button className={styles.dropdownItem} onClick={() => handleNavigation('Customise', 'smp')}>
                                Customise Your Report
                            </button>
                            <button className={styles.dropdownItem} onClick={() => handleNavigation('regional', 'smp')}>
                                Request For Regional Data
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.secondaryLinks}>
                <button
                    className={`${styles.linkItem} ${activeTopicId === 'market-snapshot' ? styles.active : ''}`}
                    onClick={() => scrollToSection('market-snapshot')}
                >
                    Market Snapshot
                </button>
                <button
                    className={`${styles.linkItem} ${activeTopicId === 'faq-section' ? styles.active : ''}`}
                    onClick={() => scrollToSection('faq-section')}
                >
                    Frequently Asked Questions
                </button>
            </div>

            <div className={styles.shareSection}>
                <h4 className={styles.shareHeading}>Share :</h4>
                <div className={styles.socialRow}>
                    <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className={`${styles.socialIcon} ${styles.facebook}`} title="Share on Facebook">
                        <FaFacebook />
                    </a>
                    <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className={`${styles.socialIcon} ${styles.linkedin}`} title="Share on LinkedIn">
                        <FaLinkedin />
                    </a>
                    <a href={shareLinks.xTwitter} target="_blank" rel="noopener noreferrer" className={`${styles.socialIcon} ${styles.twitter}`} title="Share on X">
                        <BsTwitterX />
                    </a>
                    <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className={`${styles.socialIcon} ${styles.whatsapp}`} title="Share on WhatsApp">
                        <FaWhatsapp />
                    </a>
                    <a href={shareLinks.email} className={`${styles.socialIcon} ${styles.email}`} title="Share via Email">
                        <FaEnvelope />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ReportSidebar;
